// VideoPlayer — native HTML5 video + custom controls (PRD §9.5 +
// docs/frontend_architecture.md §6 + UI brief §6.2, B2 PR3 / B2c).
//
// Native <video> with our own control bar — no Video.js / Plyr /
// heavyweight player. UI brief §6.2 calls for "framerate-aware
// scrubbing" since this is a VFX surface; the controls implement
// frame-step via `currentTime × fps` and surface a frame counter
// when fps is known. When fps is null the same controls work but
// the counter falls back to wall-clock timecode and frame-step is
// disabled (a round-second jump would be more confusing than no
// jump at all on a non-fps clip).
//
// Keyboard handling is bound to the wrapper, not the document — the
// player has to be focused (click anywhere on the surface, or tab
// to it) before it captures keystrokes. Otherwise comma/period
// would steal characters from any focused textarea on the page.
//
//   space      → play / pause
//   ← / →      → step back / forward 1 frame (or ±1s if fps unknown)
//   , / .      → step back / forward 1 frame (explicit)
//   m          → mute toggle
//   f          → fullscreen toggle
//
// State is derived from <video> events (`play`, `pause`,
// `timeupdate`, `loadedmetadata`, `ended`, `volumechange`) — we
// don't drive it from local state writes alone, because dragging
// the scrub bar mutates `video.currentTime` and the canonical
// state is whatever the element reports back.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { formatTimecode, frameAt } from './videoFrames';

interface VideoPlayerProps {
  src: string;
  mimeType?: string;
  /** Frames per second for the underlying clip. When null, frame-step
   *  falls back to ±1s and the counter renders timecode-only. */
  fps?: number | null;
  className?: string;
  /** Fires whenever the playhead moves — used by the annotation
   *  canvas (B2e) to swap the annotation set when the user scrubs to
   *  a new frame. Parent owns the fps→frame conversion since fps lives
   *  on the candidate, not the player. */
  onTimeChange?: (currentTime: number) => void;
  /** Live-review follower input (B7): when `seq` changes the element
   *  seeks to `t` and matches `playing`. Used by participants to track
   *  the host's authoritative transport. Undefined for every non-live
   *  caller, which keeps the player fully uncontrolled as before. */
  followPlayhead?: { t: number; playing: boolean; seq: number } | null;
  /** Live-review host output (B7): fires alongside `onTimeChange` with
   *  the play/pause state so the host can broadcast an authoritative
   *  transport. Undefined for every non-live caller. */
  onPlayingChange?: (playing: boolean) => void;
}

interface PlayerState {
  playing: boolean;
  currentTime: number;
  duration: number;
  muted: boolean;
  ended: boolean;
}

const INITIAL_STATE: PlayerState = {
  playing: false,
  currentTime: 0,
  duration: 0,
  muted: false,
  ended: false,
};

export function VideoPlayer({
  src,
  mimeType,
  fps,
  className,
  onTimeChange,
  followPlayhead,
  onPlayingChange,
}: VideoPlayerProps): React.JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<PlayerState>(INITIAL_STATE);
  // Hold the callback in a ref so the <video>-event effect doesn't
  // need to re-bind every time the parent's onTimeChange identity
  // changes (e.g. when it closes over a frequently-updated state).
  // useLayoutEffect commits the latest value before any event the new
  // effect listeners would fire on this paint.
  const onTimeChangeRef = useRef(onTimeChange);
  const onPlayingChangeRef = useRef(onPlayingChange);
  useLayoutEffect(() => {
    onTimeChangeRef.current = onTimeChange;
    onPlayingChangeRef.current = onPlayingChange;
  }, [onTimeChange, onPlayingChange]);

  // Fullscreen state is read off `document.fullscreenElement` — the
  // browser owns it. We mirror it locally so the button label flips
  // without an extra render hook.
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    function onChange(): void {
      setIsFullscreen(document.fullscreenElement === wrapperRef.current);
    }
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);

  const stepFrames = useCallback(
    (delta: number): void => {
      const video = videoRef.current;
      if (video === null) return;
      // When fps is known, snap to integer frame boundaries — that's
      // the frame-accurate UX a VFX user expects. When unknown, fall
      // back to ±1 second so the keys still do something useful.
      if (fps !== null && fps !== undefined && fps > 0) {
        const frame = Math.floor(video.currentTime * fps);
        const target = (frame + delta) / fps;
        video.currentTime = Math.max(0, Math.min(target, video.duration || target));
      } else {
        const target = video.currentTime + delta;
        video.currentTime = Math.max(0, Math.min(target, video.duration || target));
      }
    },
    [fps]
  );

  const togglePlay = useCallback((): void => {
    const video = videoRef.current;
    if (video === null) return;
    if (video.paused || video.ended) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback((): void => {
    const video = videoRef.current;
    if (video === null) return;
    video.muted = !video.muted;
  }, []);

  const toggleFullscreen = useCallback((): void => {
    const wrap = wrapperRef.current;
    if (wrap === null) return;
    if (document.fullscreenElement === wrap) {
      void document.exitFullscreen();
    } else {
      void wrap.requestFullscreen();
    }
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    // Don't hijack typing inside form controls — if the user tabs from
    // a comment textarea into the player, we want intent-aware keys.
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }
    switch (e.key) {
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        togglePlay();
        return;
      case 'ArrowLeft':
      case ',':
        e.preventDefault();
        stepFrames(-1);
        return;
      case 'ArrowRight':
      case '.':
        e.preventDefault();
        stepFrames(1);
        return;
      case 'm':
      case 'M':
        e.preventDefault();
        toggleMute();
        return;
      case 'f':
      case 'F':
        e.preventDefault();
        toggleFullscreen();
        return;
      default:
        return;
    }
  }

  // Bind <video> events. `loadedmetadata` is the first reliable point
  // duration is known; `timeupdate` fires ~4× per second during
  // playback; `seeked` covers manual scrubs. `ended` fires once when
  // playback hits the end so the play button can flip back.
  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;
    function sync(): void {
      if (video === null) return;
      setState({
        playing: !video.paused && !video.ended,
        currentTime: video.currentTime,
        duration: Number.isFinite(video.duration) ? video.duration : 0,
        muted: video.muted,
        ended: video.ended,
      });
      onTimeChangeRef.current?.(video.currentTime);
      onPlayingChangeRef.current?.(!video.paused && !video.ended);
    }
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('timeupdate', sync);
    video.addEventListener('seeked', sync);
    video.addEventListener('loadedmetadata', sync);
    video.addEventListener('ended', sync);
    video.addEventListener('volumechange', sync);
    sync();
    return () => {
      video.removeEventListener('play', sync);
      video.removeEventListener('pause', sync);
      video.removeEventListener('timeupdate', sync);
      video.removeEventListener('seeked', sync);
      video.removeEventListener('loadedmetadata', sync);
      video.removeEventListener('ended', sync);
      video.removeEventListener('volumechange', sync);
    };
  }, [src]);

  // When the MIME type is known we feed the URL through a <source>
  // child (see the <video> element below). Unlike the src attribute,
  // mutating a <source> child never re-runs resource selection on its
  // own - call load() explicitly so switching candidates actually
  // switches the media.
  useEffect(() => {
    if (mimeType !== undefined) videoRef.current?.load();
  }, [src, mimeType]);

  // Follow the host's authoritative transport (B7). The hook hands us a
  // new object only on a host update (bumped `seq`), so depending on the
  // object identity re-seeks exactly then and never on unrelated paints.
  useEffect(() => {
    if (followPlayhead === null || followPlayhead === undefined) return;
    const video = videoRef.current;
    if (video === null) return;
    if (Math.abs(video.currentTime - followPlayhead.t) > 0.15) {
      video.currentTime = followPlayhead.t;
    }
    if (followPlayhead.playing && (video.paused || video.ended)) {
      void video.play();
    } else if (!followPlayhead.playing && !video.paused) {
      video.pause();
    }
  }, [followPlayhead]);

  const frame = frameAt(state.currentTime, fps);
  const totalFrames = frameAt(state.duration, fps);

  const wrapperClass = [
    'flex flex-col gap-2 outline-none',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={wrapperRef}
      className={wrapperClass}
      tabIndex={0}
      onKeyDown={onKeyDown}
      role="region"
      aria-label="Video player"
    >
      <video
        ref={videoRef}
        // When the MIME type is known, deliver it via an inline
        // <source type=...> - some servers send
        // `application/octet-stream` and Chrome refuses to play
        // without the hint. The `src` ATTRIBUTE must be omitted in
        // that case: per the HTML resource-selection algorithm, a src
        // attribute on the media element makes the browser ignore
        // <source> children entirely.
        src={mimeType === undefined ? src : undefined}
        preload="metadata"
        playsInline
        className="bg-bg-muted w-full"
        onClick={togglePlay}
      >
        {mimeType !== undefined && <source src={src} type={mimeType} />}
      </video>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlay}
          className="border-border bg-bg-elevated text-text rounded-2 hover:bg-bg-muted border px-2 py-1 text-13 transition-colors"
          aria-label={state.playing ? 'Pause' : 'Play'}
        >
          {state.playing ? 'Pause' : 'Play'}
        </button>

        <button
          type="button"
          onClick={() => {
            stepFrames(-1);
          }}
          className="border-border bg-bg-elevated text-text rounded-2 hover:bg-bg-muted border px-2 py-1 text-13 transition-colors"
          aria-label="Step back one frame"
        >
          −1
        </button>
        <button
          type="button"
          onClick={() => {
            stepFrames(1);
          }}
          className="border-border bg-bg-elevated text-text rounded-2 hover:bg-bg-muted border px-2 py-1 text-13 transition-colors"
          aria-label="Step forward one frame"
        >
          +1
        </button>

        <input
          type="range"
          min={0}
          max={state.duration || 0}
          step={fps !== null && fps !== undefined && fps > 0 ? 1 / fps : 0.05}
          value={state.currentTime}
          onChange={(e) => {
            const video = videoRef.current;
            if (video === null) return;
            video.currentTime = Number(e.target.value);
          }}
          aria-label="Scrub"
          className="flex-1"
        />

        <span
          className="text-text-muted text-13 font-mono tabular-nums"
          aria-label="Playhead"
        >
          {frame !== null && totalFrames !== null
            ? `${frame} / ${totalFrames}`
            : `${formatTimecode(state.currentTime)} / ${formatTimecode(state.duration)}`}
        </span>

        <button
          type="button"
          onClick={toggleMute}
          className="border-border bg-bg-elevated text-text rounded-2 hover:bg-bg-muted border px-2 py-1 text-13 transition-colors"
          aria-label={state.muted ? 'Unmute' : 'Mute'}
          aria-pressed={state.muted}
        >
          {state.muted ? 'Muted' : 'Mute'}
        </button>

        <button
          type="button"
          onClick={toggleFullscreen}
          className="border-border bg-bg-elevated text-text rounded-2 hover:bg-bg-muted border px-2 py-1 text-13 transition-colors"
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          aria-pressed={isFullscreen}
        >
          {isFullscreen ? 'Exit FS' : 'Fullscreen'}
        </button>
      </div>
    </div>
  );
}
